import { Inject, Injectable, OnDestroy } from "@angular/core";
import { FormGroup } from "@angular/forms";

import { TranslateService } from "@ngx-translate/core";
import { Observable, Subscription, switchMap, EMPTY } from "rxjs";

import { MessageType, NotificationsService } from "@hermes/aphrodite/layout";
import { PaymentResponse } from "@hermes/api-model-payment";
import { LOCALE, StorageManager, StorageService } from "@hermes/app-core";
import { Locale } from "@hermes/locale";
import { BasketFacade } from "@hermes/states/basket";
import { ConversionFunnelFacade } from "@hermes/states/conversion-funnel";

import { UserStateService } from "@hermes/states/user";
import {
    AnalyticsService,
    CHECKOUT_PAYMENT_CTA_CLICK,
    CHECKOUT_PAYMENT_INFORMATION_ERROR,
    CHECKOUT_PAYMENT_STOCK_ERROR,
    CHECKOUT_RECURRING_CONDITIONS_REQUIRED,
    CHECKOUT_TERMS_CONDITIONS_REQUIRED,
    CheckoutErrorAnalyticsEvent,
    CheckoutPaymentAnalyticsEvent,
} from "@hermes/utils/analytics";
import { EcomErrorCodes } from "@hermes/utils-generic/constants";
import {
    FormService,
    UrlUtils,
} from "@hermes/utils-generic/services/user-interface";
import { FeatureFlagFacade, PAYMENT_FALLBACK_V1 } from "@hermes/states/flipper";

export interface ErrorMessage {
    key: string;
    translation?: string;
    params?: Record<string, string>;
}

const GLOBAL_OOPS = $localize`:@@hermes-global-translations.oops:Oops!`;
const PAYMENT_DECLINED_ERROR = $localize`:@@hermes-checkout-payment-declined-error:Oops ! Your payment was declined`;

@Injectable()
export class PaymentErrorsService implements OnDestroy {
    public messages: ErrorMessage[] = [];

    private subscriptions: Subscription = new Subscription();
    private localStorage: StorageManager | undefined;

    constructor(
        @Inject(LOCALE) public locale: Locale,
        private analyticsService: AnalyticsService,
        private conversionFunnelFacade: ConversionFunnelFacade,
        private formService: FormService,
        private translateService: TranslateService,
        public userService: UserStateService,
        private basketFacade: BasketFacade,
        private notificationsService: NotificationsService,
        public urlUtils: UrlUtils,
        public storageService: StorageService,
        private featureFlagFacade: FeatureFlagFacade,
    ) {
        this.localStorage = storageService.getLocalStorageInstance();
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public addPaymentError(
        errorMessage: ErrorMessage,
        isPersistent?: boolean,
    ): void {
        this.messages.push(errorMessage);
        if (isPersistent) {
            this.localStorage?.setItem("payment_errors", this.messages);
        }
    }

    public getPersistentMessages(): ErrorMessage[] {
        const messages =
            this.localStorage?.getItem<ErrorMessage[]>("payment_errors");
        return messages ?? [];
    }

    public reset(resetAll?: boolean): void {
        this.messages = [];
        if (resetAll) {
            this.localStorage?.deleteItem("payment_errors");
        }
    }

    public handlePaymentResponseError(
        paymentResponse: PaymentResponse,
        paymentForm: FormGroup,
    ): void {
        let shouldAddPaymentError = true;
        this.conversionFunnelFacade.setHasError(true);
        this.formService.prefixTitleOnError();

        if (
            paymentResponse.internalCode ===
            EcomErrorCodes.OUT_OF_STOCK_WHEN_PAYMENT
        ) {
            this.subscriptions.add(
                this.getTranslationOutOfStock().subscribe(
                    (translation: string) => {
                        this.addPaymentError({
                            translation,
                            key: "hermes-checkout-page.out-of-stock-angular",
                        });
                    },
                ),
            );

            this.analyticsService.sendData(
                new CheckoutErrorAnalyticsEvent({
                    eventType: CHECKOUT_PAYMENT_STOCK_ERROR,
                    sku: paymentResponse.message
                        .match(/product \w+/g)?.[0]
                        .slice(8),
                }),
            );

            // Session needs refresh : one item in the basket is out of stock in magento but the information of witch item is not set here
            // because payment response does not have basket
            this.userService.setSessionNeedsRefresh(true);
            this.basketFacade.clearData();
        }

        if (
            paymentResponse.internalCode ===
            EcomErrorCodes.ERR_PAYMENT_PROCESSING
        ) {
            this.formService.handleTitleOnError(paymentForm);
            this.analyticsService.sendData(
                new CheckoutErrorAnalyticsEvent({
                    eventType: CHECKOUT_PAYMENT_INFORMATION_ERROR,
                }),
            );
        }

        if (paymentResponse.internalCode === EcomErrorCodes.ERR_PAYMENT_KO) {
            this.subscriptions.add(
                this.featureFlagFacade
                    .isActivated(PAYMENT_FALLBACK_V1)
                    .pipe(
                        switchMap((shouldUsePaymentFallback) => {
                            if (shouldUsePaymentFallback) {
                                this.addPaymentError(
                                    {
                                        key: paymentResponse.internalCode,
                                        translation:
                                            "hermes-checkout-payment-another-card-or-payment-method",
                                    },
                                    true,
                                );
                                shouldAddPaymentError = false;
                                return EMPTY;
                            }
                            return this.notificationsService.showNotification({
                                message: {
                                    title: GLOBAL_OOPS,
                                    description: PAYMENT_DECLINED_ERROR,
                                },
                                type: MessageType.SuperCritical,
                                isTemporary: true,
                            });
                        }),
                    )
                    .subscribe(() => this.urlUtils.redirectToRootPath()),
            );
        }

        if (shouldAddPaymentError) {
            this.addPaymentError({
                key: paymentResponse.internalCode,
                translation:
                    paymentResponse?.refusalReason?.key ??
                    paymentResponse.message,
                params: paymentResponse.refusalReason?.params,
            });
        }
    }

    public sendErrorAnalyticsEvents(paymentForm: FormGroup): void {
        if (paymentForm.get("methodList.checkboxGTC")?.invalid) {
            this.analyticsService.sendData(
                new CheckoutErrorAnalyticsEvent({
                    eventType: CHECKOUT_TERMS_CONDITIONS_REQUIRED,
                }),
            );
        }

        if (paymentForm.get("methodList.checkboxRecurring")?.invalid) {
            this.analyticsService.sendData(
                new CheckoutErrorAnalyticsEvent({
                    eventType: CHECKOUT_RECURRING_CONDITIONS_REQUIRED,
                }),
            );
        }

        this.analyticsService.sendData(
            new CheckoutPaymentAnalyticsEvent({
                eventType: CHECKOUT_PAYMENT_CTA_CLICK,
                returnStatus: "fail",
            }),
        );
    }

    private getTranslationOutOfStock(): Observable<string> {
        const cart = `${this.locale.urlPrefix}/cart/`;

        return this.translateService.get("hermes.cart_page_link.text").pipe(
            switchMap((linkCart) => {
                const params = {
                    /* eslint-disable-next-line @typescript-eslint/naming-convention */
                    cart_link: `<a class="submit" href="${cart}"><U>${linkCart}</U></a>`,
                };
                return this.translateService.get(
                    "hermes-checkout-page.out-of-stock-angular",
                    params,
                );
            }),
        );
    }
}
